import { Either, ReadonlyArray, Runtime, flow } from "fp";
import React, { useMemo } from "react";
import * as ReactQuery from "react-query";

import { Company } from "~/Company";
import * as GenericFilter from "~/Filter";
import { Order } from "~/Order";
import * as Preferences from "~/Preferences";
import { Remote as GenericRemote, Remote } from "~/Remote";
import { Request } from "~/Request";
import { useSelector } from "~/State";

export type Asset = {
  readonly id: string;
  readonly name: string;
};

export type Assets = {
  readonly assets: readonly Asset[];
};

export namespace Assets {
  export type QueryResult = { readonly loading: boolean } & Assets;

  export const use = (
    queryOptions?: ReactQuery.UseQueryOptions<
      Either.ErrorOr<Assets>,
      Error,
      Either.ErrorOr<Assets>
    >
  ) =>
    Remote.when<Assets, QueryResult>(
      pipe(
        useSelector(({ companies: { current } }) =>
          useCompanyAssets(current, queryOptions)
        )
      ),
      {
        loading: () => ({ loading: true, assets: [] }),
        error: () => ({ loading: false, assets: [] }),
        success: ({ assets }) => ({ loading: false, assets }),
      }
    );

  export const usePrefetch = () => {
    Company.Assets.use();
  };

  export const useCompanyAssets = (
    { company: { id } }: Company,
    queryOptions?: ReactQuery.UseQueryOptions<
      Either.ErrorOr<Assets>,
      Error,
      Either.ErrorOr<Assets>
    >
  ): Remote<Assets> =>
    GenericRemote.Query.use(
      ["Company-Assets", id],
      Fetch.request(Company.fromID(id)),
      {
        ...(queryOptions ?? {}),
        ...{
          staleTime: 10 * 60 * 1000,
          refetchInterval: 9 * 60 * 1000,
          refetchOnWindowFocus: false,
        },
      }
    );

  export namespace Fetch {
    export const refetch = ({ company: { id } }: Company) => {
      return Remote.Fetch.use(["Company", id], request(Company.fromID(id)));
    };

    export const request = ({ company }: Company): Request.Fn<Assets> =>
      flow(
        Request.fromConfig({
          endpoint: `/elogs/companies/${company.id}/vehicles`,
          headers: {
            "X-Fields": `{ ${["asset_id", "custom_name"].join(" ")} }`,
          },
        }),
        Request.Expect.status(200),
        Request.Expect.json(Response.runtime()),
        Request.onSuccess(Response.decode)
      );
  }

  export const toString = ({ name }: Asset) => {
    return name;
  };

  export const order = () =>
    Order.fromCompare<Asset>((a, b) => {
      return Order.string().compare(
        toString(a).toLowerCase(),
        toString(b).toLowerCase()
      );
    });

  export namespace Filter {
    export type AssetIDs = readonly number[];

    type Props = {
      readonly onFilter: (assetIds: AssetIDs) => void;
    };

    export const Component = ({ onFilter }: Props): JSX.Element => {
      const processedAssets = useAssetsAsFilterOptions();

      const onClose = (allAssets: readonly GenericFilter.FilterOption[]) => {
        const selectedAssets = allAssets
          .filter(({ checked }) => checked === "on")
          .map(({ data }) => Number(data.id));

        onFilter && onFilter(selectedAssets);
      };

      return (
        <GenericFilter.Filter
          isLoading={false}
          onClose={onClose}
          label="Asset"
          initialItems={processedAssets}
        ></GenericFilter.Filter>
      );
    };

    export const useAssetFiltersFromPreferences = (): readonly [
      boolean,
      AssetIDs
    ] => {
      const [loading, companyAssets] = Remote.when(
        Preferences.Get.use<AssetIDs>(),
        {
          loading: () => [true, []],
          error: () => [false, []],
          success: (preferences) => {
            return [
              false,
              preferences[Preferences.preferenceKey("assetFilter")],
            ];
          },
        }
      );
      return [loading, companyAssets ?? []];
    };

    // eslint-disable-next-line functional/prefer-readonly-type
    export const useAssetsAsFilterOptions = (): GenericFilter.FilterOption[] => {
      const [
        preferencesLoading,
        preferences,
      ] = useAssetFiltersFromPreferences();

      const [assetsLoading, assets] = Remote.when(
        useSelector(({ companies: { current } }) => useCompanyAssets(current)),
        {
          loading: () => [true, []],
          error: () => [false, []],
          success: ({ assets }) => {
            return [false, pipe(assets, ReadonlyArray.sort(order()))];
          },
        }
      );

      const formatAsset = (asset: Asset): GenericFilter.FilterOption => {
        return {
          label: toString(asset),
          checked:
            preferences && preferences.includes(parseInt(asset.id))
              ? "on"
              : undefined,
          data: { id: String(asset.id) },
        };
      };

      const allOptions = assets;
      return useMemo(() => {
        if (preferencesLoading || assetsLoading) {
          return [];
        }

        return allOptions.map(formatAsset);
      }, [preferencesLoading, assetsLoading, preferences, allOptions.length]);
    };
  }

  export namespace Response {
    const assetRuntime = () =>
      Runtime.strict({
        asset_id: Runtime.number(),
        custom_name: Runtime.nullable(Runtime.string()),
      });

    export const runtime = () => Runtime.array(assetRuntime());

    const decodeAsset = (
      response: Runtime.ToType<typeof assetRuntime>
    ): Asset => ({
      id: `${response.asset_id}`,
      name:
        response.custom_name && response.custom_name?.trim() !== ""
          ? response.custom_name.trim()
          : `${response.asset_id}`,
    });

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Assets => ({
      assets: response.map((asset) => decodeAsset(asset)),
    });
  }
}
